import { isSolarApprovalCohortYear } from './solarAnalysisCutoff';
import { parseSourceNumber } from '../utils/sourceNumeric';

/** Column order matches EN01_santa_monica_solar_full_table deliverable. */
export const SOLAR_COHORT_TABLE_SECTORS = [
  'Residential',
  'Commercial',
  'Industrial',
  'Educational',
  'Non-Profit',
  'Other Government',
] as const;

export type PvSizeSectorRecord = {
  year: number | string;
  sector: string | null;
  total_watts: number | string;
  num_projects?: number | string | null;
  geography?: string | null;
};

function roundKw(value: number): number {
  return Math.round(value * 10) / 10;
}

function parseProjects(value: number | string | null | undefined): number {
  const n = parseInt(String(value ?? 0), 10);
  return Number.isFinite(n) ? n : 0;
}

function sectorColumnKey(sector: string, suffix: string): string {
  return `${sector}_${suffix}`;
}

/**
 * Wide annual + cumulative table by sector (kW DC and project counts), plus a
 * cumulative_total_through_latest_year summary row — same shape as
 * santa_monica_solar_all_years_sectors_with_cumulative.csv in EN01 deliverables.
 */
export function buildSolarCumulativeSectorTableFromRecords(
  records: PvSizeSectorRecord[]
): Record<string, string | number>[] {
  let rows = records;
  if (rows.some((r) => r.geography != null && String(r.geography).trim() !== '')) {
    rows = rows.filter((r) => String(r.geography ?? '').toLowerCase() === 'city');
  }

  const annualKwByYearSector = new Map<number, Map<string, number>>();
  const annualProjectsByYearSector = new Map<number, Map<string, number>>();

  for (const record of rows) {
    const year = parseInt(String(record.year), 10);
    if (!Number.isFinite(year) || !isSolarApprovalCohortYear(year)) continue;

    const sector = (record.sector || 'unknown').trim();
    const kw = (parseSourceNumber(record.total_watts) ?? 0) / 1000;
    const projects = parseProjects(record.num_projects);

    if (!annualKwByYearSector.has(year)) {
      annualKwByYearSector.set(year, new Map());
      annualProjectsByYearSector.set(year, new Map());
    }
    const kwMap = annualKwByYearSector.get(year)!;
    const projMap = annualProjectsByYearSector.get(year)!;
    kwMap.set(sector, (kwMap.get(sector) ?? 0) + kw);
    projMap.set(sector, (projMap.get(sector) ?? 0) + projects);
  }

  const sectorsSeen = new Set<string>();
  annualKwByYearSector.forEach((sectorMap) => {
    sectorMap.forEach((_, sector) => sectorsSeen.add(sector));
  });
  const extraSectors = Array.from(sectorsSeen)
    .filter((s) => !(SOLAR_COHORT_TABLE_SECTORS as readonly string[]).includes(s))
    .sort();
  const allSectors = [...SOLAR_COHORT_TABLE_SECTORS, ...extraSectors];

  const sortedYears = Array.from(annualKwByYearSector.keys()).sort((a, b) => a - b);
  if (!sortedYears.length) return [];

  const cumulativeKwBySector = new Map<string, number>();
  allSectors.forEach((sector) => cumulativeKwBySector.set(sector, 0));

  let cumulativeProjectCount = 0;
  const cohortRows: Record<string, string | number>[] = [];

  for (const year of sortedYears) {
    const yearKw = annualKwByYearSector.get(year) ?? new Map();
    const yearProj = annualProjectsByYearSector.get(year) ?? new Map();

    let totalAnnualKw = 0;
    let totalAnnualProjects = 0;

    const row: Record<string, string | number> = {
      year,
      row_type: 'cohort_year',
    };

    for (const sector of allSectors) {
      const annualKw = yearKw.get(sector) ?? 0;
      const annualProjects = yearProj.get(sector) ?? 0;
      const cumulativeKw = (cumulativeKwBySector.get(sector) ?? 0) + annualKw;
      cumulativeKwBySector.set(sector, cumulativeKw);

      row[sectorColumnKey(sector, 'annual_kw_dc')] = roundKw(annualKw);
      row[sectorColumnKey(sector, 'cumulative_kw_dc')] = roundKw(cumulativeKw);
      row[sectorColumnKey(sector, 'annual_projects')] = annualProjects;

      totalAnnualKw += annualKw;
      totalAnnualProjects += annualProjects;
    }

    cumulativeProjectCount += totalAnnualProjects;
    const totalCumulativeKw = Array.from(cumulativeKwBySector.values()).reduce((s, v) => s + v, 0);

    row.total_annual_kw_dc = roundKw(totalAnnualKw);
    row.total_cumulative_kw_dc = roundKw(totalCumulativeKw);
    row.total_annual_projects = totalAnnualProjects;
    row.total_cumulative_projects = cumulativeProjectCount;

    cohortRows.push(row);
  }

  const latestYear = sortedYears[sortedYears.length - 1];
  const summaryRow: Record<string, string | number> = {
    year: latestYear,
    row_type: 'cumulative_total_through_latest_year',
  };
  for (const sector of allSectors) {
    summaryRow[sectorColumnKey(sector, 'cumulative_kw_dc')] = roundKw(
      cumulativeKwBySector.get(sector) ?? 0
    );
  }
  summaryRow.total_cumulative_kw_dc = roundKw(
    Array.from(cumulativeKwBySector.values()).reduce((s, v) => s + v, 0)
  );
  summaryRow.total_cumulative_projects = cumulativeProjectCount;

  return [...cohortRows, summaryRow];
}
